import {Component, inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MaterialModule} from '../../../../material.module';
import {JsonPipe} from '@angular/common';
import {
  IFunctionOptions,
  IFunctionTestRequest,
  isWorkflowFunctionBindingReady,
  SyntheticFunctionTestFixture,
  workflowFunctionBindingUnavailableMessage
} from '../../../../models/function-options.model';
import {ToastService} from '../../../../services/layout/toast.service';
import {FunctionsService} from '../../../../services/objects/functions.service';

@Component({
  selector: 'app-function-test',
  templateUrl: './function-test.component.html',
  styleUrls: ['./function-test.component.scss'],
  standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, JsonPipe]
})
export class FunctionTestComponent implements OnInit {
    private fb = inject(FormBuilder);
    private functionsService = inject(FunctionsService);
    private snackBar = inject(ToastService);

  @Input() functionOption!: IFunctionOptions;
  
  testForm: FormGroup;
  parameters: any[] = [];
  testResult: any = null;
  isLoading = false;
  fixture: SyntheticFunctionTestFixture = 'success';
  readonly fixtures: {value: SyntheticFunctionTestFixture; label: string}[] = [
    {value: 'success', label: 'Success'},
    {value: 'empty', label: 'Empty result'},
    {value: 'malformed', label: 'Malformed response'},
    {value: 'delayed', label: 'Delayed response'},
    {value: 'timeout', label: 'Timeout'},
    {value: 'retryable', label: 'Retryable provider failure'},
    {value: 'permanent', label: 'Permanent provider failure'},
    {value: 'unauthorized', label: 'Unauthorized'}
  ];

    constructor() {
    this.testForm = this.fb.group({});
  }
  
  ngOnInit(): void {
    this.parseParameters();
  }
  
  parseParameters(): void {
    try {
      if (!this.functionOption || !this.functionOption.parameters) {
        return;
      }
      
      const params = JSON.parse(this.functionOption.parameters);
      
      // Reset form
      this.testForm = this.fb.group({});
      this.parameters = [];
      
      // Process parameters from OpenAPI spec, JSON Schema, or simple key-value pairs
      if (params.components && params.components.schemas) {
        // This appears to be an OpenAPI spec
        this.processOpenApiSpec(params);
      } else if (params.type === 'object' && params.properties) {
        // This appears to be a JSON Schema
        this.processOpenApiSpec(params);
      } else {
        // Treat as simple key-value pairs
        this.processSimpleParameters(params);
      }
    } catch (error) {
      console.error('Error parsing parameters', error);
      this.snackBar.showError('Error parsing function parameters', 3000);
    }
  }
  
  processOpenApiSpec(spec: any): void {
    try {
      // Check if the spec has a top-level properties key (direct schema)
      if (spec.properties) {
        // This is a direct schema object
        for (const propName in spec.properties) {
          const prop = spec.properties[propName];
          const required = spec.required && spec.required.includes(propName);
          this.addParameterToForm(propName, prop.type, required, prop.description);
        }
        return;
      }
      
      // Find request body schema if available
      let requestSchema = null;
      
      // Look for paths and operations
      if (spec.paths) {
        for (const path in spec.paths) {
          for (const method in spec.paths[path]) {
            const operation = spec.paths[path][method];
            
            // Check for requestBody
            if (operation.requestBody && operation.requestBody.content) {
              const content = operation.requestBody.content;
              if (content['application/json'] && content['application/json'].schema) {
                requestSchema = content['application/json'].schema;
                break;
              }
            }
            
            // Check for parameters
            if (operation.parameters) {
              operation.parameters.forEach((param: any) => {
                if (param.schema) {
                  this.addParameterToForm(param.name, param.schema.type, param.required, param.description);
                }
              });
            }
          }
          if (requestSchema) break;
        }
      }
      
      // Process request schema if found
      if (requestSchema) {
        if (requestSchema.$ref) {
          // Resolve reference
          const refPath = requestSchema.$ref.replace('#/', '').split('/');
          let schema = spec;
          for (const part of refPath) {
            schema = schema[part];
          }
          requestSchema = schema;
        }
        
        if (requestSchema.properties) {
          for (const propName in requestSchema.properties) {
            const prop = requestSchema.properties[propName];
            const required = requestSchema.required && requestSchema.required.includes(propName);
            this.addParameterToForm(propName, prop.type, required, prop.description);
          }
        }
      }
    } catch (error) {
      console.error('Error processing OpenAPI spec', error);
      this.snackBar.showError('Error processing OpenAPI specification', 3000);
    }
  }
  
  processSimpleParameters(params: any): void {
    for (const key in params) {
      const value = params[key];
      const type = typeof value;
      this.addParameterToForm(key, type, false, '');
    }
  }
  
  addParameterToForm(name: string, type: string, required: boolean, description: string): void {
    // Add to parameters array for UI
    this.parameters.push({
      name,
      type,
      description,
      required
    });
    
    // Add to form group
    const validators = required ? [Validators.required] : [];
    this.testForm.addControl(name, this.fb.control('', validators));
  }
  
  testFunction(): void {
    const blockedReason = this.workflowTestBlockedReason();
    if (blockedReason) {
      this.snackBar.showError(blockedReason, 3000);
      return;
    }
    if (this.testForm.invalid) {
      this.snackBar.showError('Please fill in all required fields', 3000);
      return;
    }
    
    this.isLoading = true;
    this.testResult = null;
    
    const request: IFunctionTestRequest = {
      mode: 'synthetic',
      fixture: this.fixture,
      arguments: this.testForm.value
    };

    if (!this.functionOption.id) {
      this.testResult = request.arguments;
      this.isLoading = false;
      this.snackBar.showError('Save the function before testing it', 3000);
      return;
    }

    this.functionsService.testFunctionAsync(this.functionOption.id, request)
      .subscribe(
        (response) => {
          this.testResult = response;
          this.isLoading = false;
          this.snackBar.showSuccess('Function test completed', 3000);
        },
        (error) => {
          console.error('Error executing function', error);
          this.testResult = error?.error ?? error;
          this.isLoading = false;
          const message = typeof error?.error?.message === 'string'
            ? error.error.message
            : 'Error executing function';
          this.snackBar.showError(message, 3000);
        }
      );
  }

  workflowTestBlockedReason(): string | null {
    if (this.functionOption?.type !== 'Workflow') {
      return null;
    }

    return isWorkflowFunctionBindingReady(this.functionOption.workflowBinding)
      ? null
      : workflowFunctionBindingUnavailableMessage(this.functionOption.workflowBinding);
  }
}
